/**
 * EDITORIAL / TOME I Book mapping — existing UI catalogue is the sole source.
 *
 * This is a controlled, OWNER-initiated draft-tree connection. It never
 * creates BookPages, placements, article drafts, approved releases or grants.
 * IDs are generated by the EXISTING Book CRUD API and read back from it.
 * No automatic write on mount or on opening an Editorial MASTER.
 *
 * If a network operation fails partway through, every created node remains
 * draft/invisible, and rerunning reconciles by stable per-parent slug.
 */
import {
  ALMOND_PRODUCT_ID,
  ALMOND_PUBLICATION_SPACES,
} from "./almondPublicationCatalogV1.mjs";

const BASE = "/admin/book";
const fail = (message) => { throw new Error(message); };

const assertArray = (value) => Array.isArray(value)
  ? value : fail("Книжная структура вернула некорректный ответ.");

const safeSlug = (value) => typeof value === "string" && /^[a-z0-9-]{1,120}$/.test(value);
const label = (value) => String(value ?? "").trim();

export const almondBookCatalogueCounts = Object.freeze({
  spaces: ALMOND_PUBLICATION_SPACES.length,
  categories: ALMOND_PUBLICATION_SPACES.reduce((n, s) => n + s.categories.length, 0),
});

function verifyProduct(product) {
  if (!product || product.id !== ALMOND_PRODUCT_ID || !label(product.slug)
    || product.type !== "VOLUME") {
    fail("Том I «Миндаль» не подтверждён. Создание остановлено.");
  }
  if (ALMOND_PUBLICATION_SPACES.length !== 4
    || almondBookCatalogueCounts.categories !== 31
    || ALMOND_PUBLICATION_SPACES[3].categories.length !== 0
    || !ALMOND_PUBLICATION_SPACES.every(s => safeSlug(s.key)
       && s.categories.every(c => safeSlug(c.id) && label(c.title)))) {
    fail("Каталог Тома не соответствует утверждённой структуре.");
  }
  return product;
}
async function list(api, path) {
  const { data } = await api.get(path);
  return assertArray(data);
}
function requireSingle(candidates, error) {
  if (candidates.length > 1) fail(error);
  return candidates[0] || null;
}
function validateVolume(volume, product) {
  if (volume.product_id !== ALMOND_PRODUCT_ID || volume.slug !== product.slug
     || volume.title !== product.title) {
    fail("В Book существует конфликтующий Том. Автоматически менять его нельзя.");
  }
  if (!volume.id) fail("Отсутствует настоящий идентификатор Book Volume.");
}
function validateSection(section, volumeId, spec) {
  if (!section.id || section.volume_id !== volumeId || section.slug !== spec.key
      || section.title !== spec.title) {
    fail("Book-раздел не совпадает с утверждённым содержанием Тома.");
  }
}
function validateCategory(category, volumeId, sectionId, spec) {
  if (!category.id || category.volume_id !== volumeId
      || category.section_id !== sectionId || category.slug !== spec.id
      || category.title !== spec.title) {
    fail("Book-категория не совпадает с утверждённым содержанием Тома.");
  }
}
async function findVolume(api, product) {
  const all = await list(api, `${BASE}/volumes`);
  const matches = all.filter(v => v.product_id === ALMOND_PRODUCT_ID || v.slug === product.slug);
  const found = requireSingle(matches, "Обнаружено несколько томов с одним адресом или продуктом.");
  if (found) validateVolume(found, product);
  return found;
}
async function findSections(api, volumeId) {
  const all = (await list(api, `${BASE}/sections?volume_id=${encodeURIComponent(volumeId)}`));
  const slugs = ALMOND_PUBLICATION_SPACES.map(s => s.key);
  if (all.some(s => !slugs.includes(s.slug)) || new Set(all.map(s => s.slug)).size !== all.length) {
    fail("В Book есть дополнительные или повторяющиеся разделы. Нужна ручная проверка.");
  }
  for (const spec of ALMOND_PUBLICATION_SPACES) {
    const item = all.find(s => s.slug === spec.key);
    if (item) validateSection(item, volumeId, spec);
  }
  return all;
}
async function findCategories(api, volumeId, sectionId, spec) {
  const all = (await list(api,
    `${BASE}/subsections?volume_id=${encodeURIComponent(volumeId)}&section_id=${encodeURIComponent(sectionId)}`));
  const slugs = spec.categories.map(c => c.id);
  if (all.some(c => !slugs.includes(c.slug))
      || new Set(all.map(c => c.slug)).size !== all.length) {
    fail("В Book есть дополнительные или повторяющиеся категории. Нужна ручная проверка.");
  }
  for (const wanted of spec.categories) {
    const item = all.find(c => c.slug === wanted.id);
    if (item) validateCategory(item, volumeId, sectionId, wanted);
  }
  return all;
}

/** GET-only preflight: no Mongo writes even when called repeatedly. */
export async function inspectAlmondBookTree(api, product) {
  verifyProduct(product);
  const volume = await findVolume(api, product);
  const actual = { volume: !!volume, spaces: 0, categories: 0 };
  if (!volume) return { ...actual, missingSpaces: 4, missingCategories: 31 };
  const sections = await findSections(api, volume.id);
  actual.spaces = sections.length;
  for (const spec of ALMOND_PUBLICATION_SPACES) {
    const s = sections.find(item => item.slug === spec.key);
    if (s) actual.categories += (await findCategories(api, volume.id, s.id, spec)).length;
  }
  return { ...actual,
    missingSpaces: almondBookCatalogueCounts.spaces - actual.spaces,
    missingCategories: almondBookCatalogueCounts.categories - actual.categories };
}

/** POST occurs only in the explicit Owner confirmation handler. */
export async function connectAlmondBookTree(api, product) {
  verifyProduct(product);
  await inspectAlmondBookTree(api, product); // fail closed BEFORE the first write

  let volume = await findVolume(api, product);
  if (!volume) {
    const { data } = await api.post(`${BASE}/volumes`, {
      slug: product.slug, title: product.title, product_id: ALMOND_PRODUCT_ID,
      status: "draft", sort_order: 0,
    });
    volume = data;
    validateVolume(volume, product);
  }

  for (const [order, spec] of ALMOND_PUBLICATION_SPACES.entries()) {
    let sections = await findSections(api, volume.id);
    let section = sections.find(s => s.slug === spec.key);
    if (!section) {
      const { data } = await api.post(`${BASE}/sections`, {
        volume_id: volume.id, slug: spec.key, title: spec.title,
        subtitle: spec.subtitle, status: "draft", sort_order: order,
      });
      section = data;
      validateSection(section, volume.id, spec);
    }
    let categories = await findCategories(api, volume.id, section.id, spec);
    for (const [categoryOrder, category] of spec.categories.entries()) {
      if (categories.some(c => c.slug === category.id)) continue;
      const { data } = await api.post(`${BASE}/subsections`, {
        volume_id: volume.id, section_id: section.id,
        slug: category.id, title: category.title,
        status: "draft", sort_order: categoryOrder,
      });
      validateCategory(data, volume.id, section.id, category);
      categories = [...categories, data];
    }
  }

  const state = await inspectAlmondBookTree(api, product);
  if (state.missingSpaces || state.missingCategories) {
    fail("Связь Book создана не полностью. Действие безопасно повторить.");
  }
  return { ...state, bookVolumeId: volume.id };
}
